import { formatJobMetadata, specialJobFamilyLabels } from 'constants/roleLevel';
import {
  ContentRoleLevel,
  DuplicateJobRoleModalPaginatedFragment,
  JobRoleFindManyPaginatedOrderField,
  JobRoleSource,
  SortOption,
  useDuplicateJobRoleModalFindManyPaginatedQuery,
  useProjectJobRoleFindManyQuery,
} from 'generated/graphql';
import { formatDate, formatISO } from 'helpers/date';
import { debounce } from 'lodash';
import uniqBy from 'lodash/uniqBy';
import { useEffect, useState } from 'react';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

export const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_TIME = 500;

export const JOB_ROLE_SOURCE_LABELS: Record<JobRoleSource, string> = {
  [JobRoleSource.SzAdmin]: 'Admin app',
  [JobRoleSource.RoleRequirements]: 'Company app',
};

export const JOB_ROLE_ORDER_BY_LABELS: Record<
  JobRoleFindManyPaginatedOrderField,
  string
> = {
  [JobRoleFindManyPaginatedOrderField.CompanyName]: 'Company name',
  [JobRoleFindManyPaginatedOrderField.Name]: 'Name',
  [JobRoleFindManyPaginatedOrderField.CreatedAt]: 'Created at',
  [JobRoleFindManyPaginatedOrderField.UpdatedAt]: 'Updated at',
};

export type TDuplicateJobRoleFilters = {
  source?: JobRoleSource;
  roleLevel?: ContentRoleLevel;
  createdAtFrom?: string;
  createdAtTo?: string;
  orderBy?: JobRoleFindManyPaginatedOrderField;
};

export type TDuplicableJobRole = {
  id: number;
  name: string;
  roleLevel: ContentRoleLevel;
  levelLabel: string;
  familyLabel: string;
  sourceLabel: string;
  createdLabel: string;
  qualifier: string;
  disabled: boolean;
  raw: DuplicateJobRoleModalPaginatedFragment['data'][number];
};

// Default provenance note prefilled into the "Additional information" field and
// used as the fallback at confirm time — keep both in sync via this builder.
export const buildDuplicationQualifier = (
  role: Pick<TDuplicableJobRole, 'id' | 'name'>,
) => `Duplicated from ${role.name} (#${role.id})`;

type UseGetDuplicableJobRolesProps = {
  companyId: number;
  projectId: number;
};

function toDuplicableJobRole(
  jobRole: DuplicateJobRoleModalPaginatedFragment['data'][number],
): TDuplicableJobRole {
  // A role can only be connected if its first RSP actually has skills to show —
  // mirrors the preview's "No skills to display" empty state. Covers "no
  // profile" too (undefined firstProfile → zero skills → disabled).
  const firstProfile = jobRole.skillsProfiles[0];
  const hasSkillsToShow =
    (firstProfile?.successProfileSoftSkills?.length ?? 0) +
      (firstProfile?.successProfileTechnicalSkills?.length ?? 0) >
    0;

  return {
    id: jobRole.id,
    name: jobRole.name,
    roleLevel: jobRole.roleLevel,
    levelLabel: formatJobMetadata(jobRole.roleLevel),
    familyLabel: jobRole.family
      ? specialJobFamilyLabels[jobRole.family] ??
        formatJobMetadata(jobRole.family)
      : '',
    sourceLabel: JOB_ROLE_SOURCE_LABELS[jobRole.source],
    createdLabel: formatDate(formatISO(jobRole.createdAt), 'dd MMM yyyy'),
    qualifier: jobRole.qualifier ?? '',
    disabled: !hasSkillsToShow,
    raw: jobRole as unknown as DuplicateJobRoleModalPaginatedFragment['data'][number],
  } as TDuplicableJobRole;
}

export function useGetDuplicableJobRoles({
  companyId,
  projectId,
}: UseGetDuplicableJobRolesProps) {
  const { handleMsgType } = useNotification();
  const [jobRoles, setJobRoles] = useState<
    DuplicateJobRoleModalPaginatedFragment['data']
  >([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | undefined>();
  const [searchValue, setSearchValue] = useState('');
  const [appliedFilters, setAppliedFilters] =
    useState<TDuplicateJobRoleFilters>({});

  const { data: projectJobRolesData, loading: projectJobRolesLoading } =
    useProjectJobRoleFindManyQuery({
      variables: {
        id: projectId,
      },
      onError: (error) => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
        });
      },
    });

  // When the project already has roles, only roles of the same role level can
  // be connected (a duplicated role inherits the original's level), so we lock
  // the level server-side. An empty project leaves this undefined → the user can
  // choose a level via the filters.
  const currentlyActiveJobRolesLevel =
    projectJobRolesData?.ProjectJobRoleFindMany?.[0]?.jobRole?.roleLevel;

  const roleLevel = currentlyActiveJobRolesLevel ?? appliedFilters.roleLevel;

  const filters = {
    companyId,
    search: searchValue,
    ...(roleLevel && { roleLevel }),
    ...(appliedFilters.source && { source: appliedFilters.source }),
    ...(appliedFilters.createdAtFrom && {
      createdAtFrom: appliedFilters.createdAtFrom,
    }),
    ...(appliedFilters.createdAtTo && {
      createdAtTo: appliedFilters.createdAtTo,
    }),
  };

  // Name reads naturally A→Z; dates default to newest-first. When nothing is
  // picked we omit `order` so the BE applies its default (created-at, newest).
  const order = appliedFilters.orderBy
    ? {
        field: appliedFilters.orderBy,
        direction:
          appliedFilters.orderBy === JobRoleFindManyPaginatedOrderField.Name
            ? SortOption.Asc
            : SortOption.Desc,
      }
    : undefined;

  const { loading: loadingJobRoles, data: jobRolesData } =
    useDuplicateJobRoleModalFindManyPaginatedQuery({
      // Wait until we know the project's role level before fetching, so we never
      // briefly show roles of the wrong level.
      skip: projectJobRolesLoading,
      fetchPolicy: 'cache-and-network',
      variables: {
        filters,
        ...(order && { order }),
        pagination: { page, size: PAGE_SIZE },
      },
      onError: (error) => {
        handleMsgType({
          type: TNotification.error,
          message:
            error?.message || 'Oops, some issue happened while getting roles',
        });
      },
    });

  useEffect(() => {
    if (jobRolesData) {
      const resultJobRoles = jobRolesData?.JobRoleFindManyPaginated.data || [];
      const pageInfo = jobRolesData?.JobRoleFindManyPaginated.page;

      // Page 1 means the search/filters changed → replace; later pages append.
      setJobRoles((prev) =>
        pageInfo?.currentPage && pageInfo.currentPage > 1
          ? uniqBy([...prev, ...resultJobRoles], 'id')
          : resultJobRoles,
      );
      setTotalPages(pageInfo?.pageTotal);
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [jobRolesData]);

  // Reset to a fresh first page whenever the search or filters change.
  const resetToFirstPage = () => {
    setJobRoles([]);
    setPage(1);
    setLoading(true);
  };

  const search = (inputValue: string) => {
    if (inputValue === searchValue) return;
    setSearchValue(inputValue);
    resetToFirstPage();
  };

  const debouncedSearch = debounce(search, SEARCH_DEBOUNCE_TIME);

  const applyFilters = (next: TDuplicateJobRoleFilters) => {
    setAppliedFilters(next);
    resetToFirstPage();
  };

  const loadMore = () => {
    setLoading(true);
    setPage((prev) => prev + 1);
  };

  const duplicableJobRoles = jobRoles.map(toDuplicableJobRole);

  const activeFilterCount =
    (appliedFilters.source ? 1 : 0) +
    (appliedFilters.createdAtFrom || appliedFilters.createdAtTo ? 1 : 0) +
    (!currentlyActiveJobRolesLevel && appliedFilters.roleLevel ? 1 : 0);

  return {
    jobRoles: duplicableJobRoles,
    count: duplicableJobRoles.length,
    itemsTotal: jobRolesData?.JobRoleFindManyPaginated.page?.itemsTotal,
    loading: loadingJobRoles || projectJobRolesLoading || loading,
    currentlyActiveJobRolesLevel,
    activeLevelLabel: currentlyActiveJobRolesLevel
      ? formatJobMetadata(currentlyActiveJobRolesLevel)
      : undefined,
    hasMore: Boolean(totalPages && page < totalPages),
    searchValue,
    debouncedSearch,
    loadMore,
    appliedFilters,
    applyFilters,
    activeFilterCount,
  };
}
